import { ANALYTICS_AREAS, ANALYTICS_GRAINS, type AnalyticsArea } from '@wodi/domain';
import { z } from 'zod';

import type { Permission } from '../permissions.ts';
import { isoDaySchema } from './clinical-common.ts';
import { reportPeriodSchema } from './reports.ts';

// Deeper analytics (docs/plans/phase3-deeper-analytics.md §4). One view shape for every
// area, so the screen and the CSV writer are generic. Filters travel in the request body,
// never the URL. Cells are strings: counts and durations as plain decimals, money as cents
// (ADR 0006), instants as ISO strings shown in EAT by the client. Row keys are IDs or codes,
// never names or anything typed about a patient.

export { ANALYTICS_AREAS };

/**
 * What each area needs (plan A2): `read` for its dashboard, `drill` for the rows behind a
 * figure. `drillListsVisits` drill-downs are audited as `report.viewed` (A11). Downloading
 * the aggregate also needs `reports.export`.
 */
export const ANALYTICS_ACCESS: Record<
  AnalyticsArea,
  {
    readonly title: string;
    readonly description: string;
    readonly read: readonly Permission[];
    readonly drill: readonly Permission[];
    readonly drillListsVisits: boolean;
  }
> = {
  flow: {
    title: 'Patient flow',
    description: 'Visits, new patients and waiting at each queue, over a period.',
    read: ['analytics.read'],
    drill: ['analytics.read', 'reports.registers.read'],
    drillListsVisits: true,
  },
  revenue: {
    title: 'Revenue',
    description: 'Billed and collected over a period, by department and payer.',
    read: ['analytics.read', 'reports.finance.read'],
    drill: ['analytics.read', 'reports.finance.read'],
    drillListsVisits: false,
  },
  claims: {
    title: 'Claims ageing',
    description: 'SHA and insurer claims still unpaid, by payer and age.',
    read: ['analytics.read', 'reports.claims.read'],
    drill: ['analytics.read', 'reports.claims.read'],
    drillListsVisits: false,
  },
  stock: {
    title: 'Pharmacy and stock',
    description: 'What was used, what expires soon and what is out of stock.',
    read: ['analytics.read', 'stock.read'],
    drill: ['analytics.read', 'stock.read'],
    drillListsVisits: false,
  },
  lab: {
    title: 'Lab turnaround',
    description: 'Requests and turnaround over a period, by section and test.',
    read: ['analytics.read', 'lab.read'],
    drill: ['analytics.read', 'lab.read', 'reports.registers.read'],
    drillListsVisits: true,
  },
  inpatient: {
    title: 'Inpatient',
    description: 'Bed occupancy, admissions and length of stay, by ward.',
    read: ['analytics.read', 'inpatient.read'],
    drill: ['analytics.read', 'inpatient.read', 'reports.registers.read'],
    drillListsVisits: true,
  },
};

/** How a cell is read and shown. */
export const ANALYTICS_UNITS = [
  'count',
  'minutes',
  'days',
  'percent',
  'money',
  'number',
  'text',
  /** An identifier: visit, patient or batch number, shown in mono. */
  'id',
  'instant',
  'day',
] as const;
export type AnalyticsUnit = (typeof ANALYTICS_UNITS)[number];

// --- Requests ----------------------------------------------------------------------------

export const analyticsQuerySchema = z.object({
  /** Omitted: the facility the reader is working at. */
  facilityId: z.uuid().optional(),
  period: reportPeriodSchema,
  /** The area's department: a service point, category, store, section or ward (A5). */
  department: z
    .string()
    .regex(/^[a-z0-9_-]{1,64}$/)
    .optional(),
});
export type AnalyticsQuery = z.infer<typeof analyticsQuerySchema>;

export const analyticsDrillQuerySchema = analyticsQuerySchema.extend({
  table: z.string().regex(/^[a-z_]{1,40}$/),
  rowKey: z.string().regex(/^[a-z0-9_-]{1,64}$/),
  cursor: z.string().max(200).optional(),
});
export type AnalyticsDrillQuery = z.infer<typeof analyticsDrillQuerySchema>;

// --- Views -------------------------------------------------------------------------------

const optionSchema = z.object({ id: z.string(), label: z.string() });

export const analyticsSetupSchema = z.object({
  facilities: z.array(
    z.object({
      facilityId: z.uuid(),
      name: z.string(),
      current: z.boolean(),
      areas: z.array(
        z.object({
          area: z.enum(ANALYTICS_AREAS),
          drill: z.boolean(),
          export: z.boolean(),
        }),
      ),
      departments: z.object({
        flow: z.array(optionSchema),
        revenue: z.array(optionSchema),
        stock: z.array(optionSchema),
        lab: z.array(optionSchema),
        inpatient: z.array(optionSchema),
      }),
    }),
  ),
});
export type AnalyticsSetupView = z.infer<typeof analyticsSetupSchema>;

export const analyticsColumnSchema = z.object({
  key: z.string(),
  label: z.string(),
  unit: z.enum(ANALYTICS_UNITS),
});
export type AnalyticsColumn = z.infer<typeof analyticsColumnSchema>;

const rowSchema = z.object({
  /** An ID or a code (never PHI): what a drill-down asks for. */
  key: z.string(),
  /** One per column; null when there is nothing to measure. */
  cells: z.array(z.string().nullable()),
});

export const analyticsTableSchema = z.object({
  key: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  columns: z.array(analyticsColumnSchema),
  rows: z.array(rowSchema),
  /** The drill-down button's label ("Show list"), or null when rows can't be opened. */
  drillLabel: z.string().nullable(),
  /** More rows exist than are shown (a "top N" table). */
  truncated: z.boolean(),
});
export type AnalyticsTable = z.infer<typeof analyticsTableSchema>;

export const analyticsViewSchema = z.object({
  area: z.enum(ANALYTICS_AREAS),
  facility: z.object({ facilityId: z.uuid(), name: z.string(), code: z.string().nullable() }),
  period: reportPeriodSchema,
  grain: z.enum(ANALYTICS_GRAINS),
  buckets: z.array(
    z.object({ from: isoDaySchema, to: isoDaySchema, label: z.string(), title: z.string() }),
  ),
  /** When the snapshot was read. */
  dataAsAt: z.iso.datetime(),
  /**
   * The reader isn't granted every active sensitivity label, so patient-derived figures
   * leave out visits they may not see (plan §3, A7). About the reader, never the data.
   */
  labelsLimited: z.boolean(),
  summary: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      unit: z.enum(ANALYTICS_UNITS),
      value: z.string().nullable(),
    }),
  ),
  series: z.array(
    z.object({
      key: z.string(),
      title: z.string(),
      unit: z.enum(ANALYTICS_UNITS),
      /** One per bucket. */
      points: z.array(z.string().nullable()),
    }),
  ),
  tables: z.array(analyticsTableSchema),
});
export type AnalyticsView = z.infer<typeof analyticsViewSchema>;

export const analyticsDrillSchema = z.object({
  title: z.string(),
  columns: z.array(analyticsColumnSchema),
  rows: z.array(rowSchema),
  nextCursor: z.string().nullable(),
});
export type AnalyticsDrillView = z.infer<typeof analyticsDrillSchema>;
